import { ShoppingCart, Check, X, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import { useState } from 'react';

const WHATSAPP = '5591984036071'; // número da FastFix

export const ProductCard = ({ product }) => {
  const { addItem, items } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [nome, setNome] = useState('');
  const [qtd, setQtd] = useState(1);

  const isInCart = items.some(item => item.id === product.id);
  const cartItem = items.find(item => item.id === product.id);

  const handleAddToCart = () => {
    if (!product.in_stock) {
      setShowModal(true);
      return;
    }
    setIsAdding(true);
    addItem(product);
    toast.success(`${product.name} adicionado ao carrinho!`);
    setTimeout(() => setIsAdding(false), 500);
  };

  const handleEncomenda = () => {
    if (!nome.trim()) { return; }
    const msg = encodeURIComponent(
      `Olá! Gostaria de encomendar:\n\n` +
      `*Produto:* ${product.name}\n` +
      `*Quantidade:* ${qtd}x\n` +
      `*Preço unitário:* R$ ${product.price.toFixed(2)}\n` +
      `*Meu nome:* ${nome}\n\n` +
      `Pode me informar a previsão de disponibilidade?`
    );
    window.open(`https://wa.me/${WHATSAPP}?text=${msg}`, '_blank');
    setShowModal(false);
    setNome('');
    setQtd(1);
  };

  return (
    <>
      <div
        className={`group relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden hover:shadow-xl hover:border-blue-500/30 transition-all duration-300 product-card ${!product.in_stock ? 'opacity-75' : ''}`}
        data-testid={`product-card-${product.id}`}
      >
        {/* Image */}
        <div className="product-image-container">
          <img src={product.image} alt={product.name} className="w-full h-full object-contain" loading="lazy"/>
          {!product.in_stock && (
            <span className="badge-out-of-stock">Esgotado</span>
          )}
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <span className="text-xs font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">
            {product.category}
          </span>
          <h3 className="font-secondary font-semibold text-slate-900 dark:text-white line-clamp-2 min-h-[2.5rem]">
            {product.name}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 min-h-[2.5rem]">
            {product.description}
          </p>
          <div className="flex items-center justify-between pt-2">
            <p className="text-2xl font-bold text-slate-900 dark:text-white font-secondary">
              R$ {product.price.toFixed(2)}
            </p>
            {product.in_stock ? (
              <Button onClick={handleAddToCart} disabled={isAdding}
                className={`rounded-lg transition-all duration-200 ${isInCart ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white shadow-lg`}
                size="sm" data-testid={`add-to-cart-${product.id}`}>
                {isAdding ? <Check className="w-4 h-4"/> : isInCart ? (
                  <><Check className="w-4 h-4 mr-1"/><span className="hidden sm:inline">{cartItem?.quantity}</span></>
                ) : (
                  <><ShoppingCart className="w-4 h-4 sm:mr-1"/><span className="hidden sm:inline">Adicionar</span></>
                )}
              </Button>
            ) : (
              <Button onClick={() => setShowModal(true)} size="sm"
                className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white shadow-lg transition-all">
                <MessageCircle className="w-4 h-4 sm:mr-1"/>
                <span className="hidden sm:inline">Encomendar</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Modal de encomenda */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white text-lg">Encomendar Produto</h3>
                <p className="text-xs text-slate-500 mt-0.5">Produto esgotado no momento</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X className="w-5 h-5"/>
              </button>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3 flex gap-3 items-center">
              <img src={product.image} alt={product.name} className="w-14 h-14 object-contain rounded-lg bg-white dark:bg-slate-700 p-1 flex-shrink-0"/>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 dark:text-white text-sm line-clamp-2">{product.name}</p>
                <p className="text-blue-600 dark:text-blue-400 font-bold text-sm mt-0.5">R$ {product.price.toFixed(2)}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block font-medium">Seu nome *</label>
                <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: João Silva"
                  className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block font-medium">Quantidade</label>
                <div className="flex items-center gap-3">
                  <button onClick={() => setQtd(q => Math.max(1, q-1))}
                    className="w-9 h-9 rounded-full border border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-bold text-lg transition-colors">−</button>
                  <span className="font-bold text-lg text-slate-900 dark:text-white w-8 text-center">{qtd}</span>
                  <button onClick={() => setQtd(q => q+1)}
                    className="w-9 h-9 rounded-full border border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-bold text-lg transition-colors">+</button>
                  <span className="text-xs text-slate-400">= <strong className="text-slate-700 dark:text-slate-200">R$ {(product.price * qtd).toFixed(2)}</strong></span>
                </div>
              </div>
            </div>

            <div className="pt-1 space-y-2">
              <button onClick={handleEncomenda} disabled={!nome.trim()}
                className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors text-sm">
                <MessageCircle className="w-5 h-5"/>
                Chamar no WhatsApp
              </button>
              <p className="text-xs text-center text-slate-400">Você será redirecionado para o WhatsApp da FastFix</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
